/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import type { FC, ReactNode } from 'react';
import React, { useCallback, useEffect, useState, useMemo, useContext } from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { usePreviousState } from '@atlaskit/editor-common/hooks';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ButtonGroup from '@atlaskit/button/button-group';
import Button, { LoadingButton } from '@atlaskit/button';
import { ModalFooter, ModalBody, ModalTitle, ModalHeader } from '@atlaskit/modal-dialog';

import { expValEquals } from '@confluence/feature-experiments';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance';
import { keys as localStorageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey';
import { LazyArchivePageAutomationDiscovery } from '@confluence/automation-discovery/entry-points';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { useSessionData } from '@confluence/session-data';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { useChoreographerAssets } from '@confluence/choreographer-services';
import { ReverseTrialLozengeExperiment } from '@confluence/trial-components';

import { fireUIClickEvent } from './AnalyticsEvents';
import type { ArchiveDialogPropsWithContentId } from './ArchiveProcessContainer';
import { getExperienceName } from './ArchiveProgressReport';
import type {
	ArchiveDialogBaseComponentQuery as ArchiveDialogBaseComponentQuery$data,
	ArchiveDialogBaseComponentQueryVariables as ArchiveDialogBaseComponentQuery$variables,
} from './__types__/ArchiveDialogBaseComponentQuery';
import type {
	ArchiveDialogBaseComponentCheckSpaceAdminQuery as ArchiveDialogBaseComponentCheckSpaceAdminQuery$data,
	ArchiveDialogBaseComponentCheckSpaceAdminQueryVariables as ArchiveDialogBaseComponentCheckSpaceAdminQuery$variables,
} from './__types__/ArchiveDialogBaseComponentCheckSpaceAdminQuery';
import type { ArchivePagesMutationVariables } from './__types__/ArchivePagesMutation';
import type { UnarchivePagesMutationVariables } from './__types__/UnarchivePagesMutation';
import { BULK_ARCHIVE_PAGE_SELECTION_LIMIT } from './archivingConstants';
// right-align the dialog actions in the footer
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const DialogActionsContainer = styled.div({
	display: 'flex',
	justifyContent: 'flex-end',
	width: '100%',
});

export const i18n = defineMessages({
	cancelButtonText: {
		id: 'archive-pages.archive.dialog.button.cancel',
		defaultMessage: 'Cancel',
		description: 'Button text for the cancel button on the page archival dialog',
	},
	ARCHIVE_withoutNestedPagesTitle: {
		id: 'archive-pages.without-nested-pages.archive.dialog.title',
		defaultMessage: 'This is a parent item to other content items',
		description:
			'The title of the Archive Page modal dialog when user cannot archive any nested pages.',
	},
	ARCHIVE_singlePageWithNoteV2: {
		id: 'archive-pages.single.archive.dialog.title.v2',
		defaultMessage: 'Archive item',
		description:
			'The title of the Archive Item modal dialog when the user is archiving a single item.',
	},
	ARCHIVE_nestedPagesButtonText: {
		id: 'archive-pages.archive-nested-pages.dialog.button.archive',
		defaultMessage: 'Archive',
		description: 'Button text for the nested pages archival dialog primary CTA',
	},
	ARCHIVE_archiveAnywayButtonText: {
		id: 'archive-pages.archive.dialog.button.archive',
		defaultMessage: 'Archive anyway',
		description: 'Button text for the page archival dialog primary CTA',
	},
	ARCHIVE_singleArchiveButtonText: {
		id: 'archive-pages.archive.single.dialog.button.archive',
		defaultMessage: 'Archive',
		description: 'Button text for the single page archival dialog primary CTA',
	},
	ARCHIVE_NextButtonText: {
		id: 'archive-pages.archive.single.dialog.button.next',
		defaultMessage: 'Next',
		description: 'Button text to lead user to the next modal.',
	},
	ARCHIVE_archiveParentButtonSelectionLimitReachedText: {
		id: 'archive-pages.archive-nested-pages.dialog.selection-limit-reached.button.archive',
		defaultMessage: 'Archive parent',
		description:
			'Button text for nested pages when archival selection limit reached as we do not allow archiving any of the nested pages',
	},
	ARCHIVE_nestedPagesTitleGeneric: {
		id: 'archive-pages.nested-pages.archive.dialog.title.generic.M2',
		defaultMessage: 'Archive parent item',
		description: 'The title of the Archive Content modal dialog',
	},
	UNARCHIVE_pageDialogTitle: {
		id: 'archive-pages.single-page.unarchive.dialog.title',
		defaultMessage: 'Choose destination',
		description:
			'The title of the Unarchive Page modal dialog prompting them to select a parent page',
	},
	UNARCHIVE_buttonText: {
		id: 'archive-pages.single-page.unarchive.dialog.button.unarchive',
		defaultMessage: 'Restore',
		description:
			'Button text for the primary CTA in the un-archive dialog that allows user to restore an archived page',
	},
	UNARCHIVE_restoreParentButtonSelectionLimitReachedText: {
		id: 'archive-pages.archive-nested-pages.dialog.selection-limit-reached.button.unarchive',
		defaultMessage: 'Restore parent',
		description:
			'Button text for nested pages when archival selection limit reached as we do not allow restoring any of the nested pages',
	},
});

const buttonSubjectIdPrefix = (isArchive: boolean): string =>
	isArchive ? 'archiveNestedPage' : 'restorePageDestination';

type ArchiveDialogInnerProps = {
	getAdditionalMutationVariables?: Function;
	getAdditionalAnalyticsAttributes?: Function;
	children?: ReactNode;
	descriptionText?: MessageDescriptor;
	isArchive: boolean;
	isSingleArchive?: boolean;
	successLinkHref: string;
	appearance?: 'warning' | 'danger' | 'primary' | undefined;
	width?: number;
	allowNestedPages: boolean;
	includeNestedPages: boolean | null;
	// When this is the 2nd page of the restoring (unarchiving) flow where the user sets the
	// destination for restored page branch, if we are allowed to restore nested pages but the number
	// of descendants is over the selection limit, the parent component informs us that we don't need
	// to restore the descendants by passing `allowNestedPages` == false.  But in this case we want
	// to change the text of the primary action button from "Restore" to "Restore parent" - so we
	// simply pass is this data rather than run the query again to get it.
	isSelectionLimitReached?: boolean;
	isSubmitDisabled?: boolean;
	shouldUseAutomationDiscovery?: boolean;
	archivingError?: Error;
	isArchiveInProgress?: boolean;
	// This is only necessary for archive, not inarchive.
	setIsNestedActionsDialogNotePageEnabled?: (boolean) => void;
	isNestedActionsDialogNotePageEnabled?: boolean;
};

type ArchiveDialogBaseComponentMutationVariables =
	| ArchivePagesMutationVariables
	| UnarchivePagesMutationVariables;

export const ArchiveDialogBaseComponent: FC<
	ArchiveDialogInnerProps &
		ArchiveDialogPropsWithContentId<ArchiveDialogBaseComponentMutationVariables>
> = ({
	// ArchiveDialogInnerProps:
	getAdditionalMutationVariables,
	getAdditionalAnalyticsAttributes,
	children,
	descriptionText,
	appearance,
	width = 520,
	allowNestedPages,
	includeNestedPages,
	isSingleArchive,
	isSelectionLimitReached: isOverSelectionLimit = false,
	isSubmitDisabled = false,

	// ArchiveDialogPropsWithContentId:
	contentId,
	analyticsSource,
	isArchive,
	startMutationCallback,
	shouldUseAutomationDiscovery = false,
	archivingError,
	isArchiveInProgress,
	setIsNestedActionsDialogNotePageEnabled,
	isNestedActionsDialogNotePageEnabled,
}: ArchiveDialogInnerProps &
	ArchiveDialogPropsWithContentId<ArchiveDialogBaseComponentMutationVariables>) => {
	const [isOpen, setIsOpen] = useState<boolean>(true);
	const [isPageMutationStarted, setIsPageMutationStarted] = useState<boolean>(false);
	const [isAutomationDiscoveryMessageVisible, setIsAutomationDiscoveryMessageVisible] =
		useState<boolean>(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const spaceKey = useSpaceKey();
	const hasUserSeenAutomationDiscoveryMessage = localStorage.getItemAsBoolean(
		localStorageKeys.AUTOMATION_DISCOVER_ARCHIVE_FROM_OVERFLOW_MENU,
	);
	const { edition } = useSessionData();
	const { Modal } = useChoreographerAssets();

	const previousIsArchiveInProgress = usePreviousState(isArchiveInProgress);
	useEffect(() => {
		if (previousIsArchiveInProgress === true && !isArchiveInProgress) {
			setIsPageMutationStarted(false);
		}

		if (archivingError) {
			setIsOpen(false);
		}
	}, [isArchiveInProgress, archivingError, previousIsArchiveInProgress]);

	const { data: spaceAdminData, error: checkSpaceAdminError } = useQuery<
		ArchiveDialogBaseComponentCheckSpaceAdminQuery$data,
		ArchiveDialogBaseComponentCheckSpaceAdminQuery$variables
	>(
		gql`
			query ArchiveDialogBaseComponentCheckSpaceAdminQuery($spaceKey: String) {
				space(key: $spaceKey) {
					id
					currentUser {
						isAdmin
					}
				}
			}
		`,
		{
			variables: {
				spaceKey,
			},
			skip: !spaceKey || edition !== ConfluenceEdition.PREMIUM,
		},
	);

	const needQueryData = allowNestedPages;
	const { loading, error, data } = useQuery<
		ArchiveDialogBaseComponentQuery$data,
		ArchiveDialogBaseComponentQuery$variables
	>(
		gql`
			query ArchiveDialogBaseComponentQuery($contentId: ID!) {
				singleContent(id: $contentId) {
					id
					archivableDescendantsCount
					visibleDescendantsCount
				}
			}
		`,
		{
			variables: { contentId },
			skip: !needQueryData,
		},
	);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const countNestedPages = data?.singleContent?.visibleDescendantsCount || 0;
	const isSelectionLimitReached =
		isOverSelectionLimit || countNestedPages + 1 > BULK_ARCHIVE_PAGE_SELECTION_LIMIT;
	const isSpaceAdmin = Boolean(spaceAdminData?.space?.currentUser.isAdmin);

	const fireButtonClickEvent = useCallback(
		(buttonName: string, isArchive: boolean, analyticsSource: string, attributes?: object) => {
			fireUIClickEvent({
				createAnalyticsEvent,
				actionSubject: 'button',
				actionSubjectId: `${buttonSubjectIdPrefix(isArchive)}${buttonName}Button`,
				source: analyticsSource,
				...(attributes && { attributes }),
			});
		},
		[createAnalyticsEvent],
	);

	const handleStartArchive = useCallback(() => {
		// If we are not using automationDiscovery or the user has already seen automation discover,
		// the modal should close after archiving.
		const shouldClose =
			!shouldUseAutomationDiscovery ||
			!isSpaceAdmin ||
			checkSpaceAdminError ||
			hasUserSeenAutomationDiscoveryMessage;
		if (shouldClose) {
			setIsOpen(false);
		} else {
			setIsPageMutationStarted(true);
		}
		const additionalMutationVariables = getAdditionalMutationVariables
			? getAdditionalMutationVariables()
			: null;
		const parentPageId: string | null =
			(additionalMutationVariables && additionalMutationVariables.parentPageId) || null;

		const additionalEventAttributes = getAdditionalAnalyticsAttributes
			? getAdditionalAnalyticsAttributes()
			: {};

		const eventAttributes = {
			// (note that "includNestedPages" is null as a default, but is equivalent to false)
			includeNestedPages: Boolean(includeNestedPages),
			...additionalEventAttributes,
			...(!isArchive && { parentPageId }),
		};
		fireButtonClickEvent('Confirm', isArchive, analyticsSource, eventAttributes);

		const mutationVariables = isArchive
			? {
					input: [
						{
							pageID: contentId,
							...additionalMutationVariables,
						},
					],
				}
			: {
					pageIDs: [contentId],
					...additionalMutationVariables,
				};

		startMutationCallback(
			mutationVariables,
			additionalEventAttributes,
			// If we are using automationDiscovery and the user has not seen the message,
			// this boolean is used to turn off the flag in the lower left corner.
			shouldUseAutomationDiscovery &&
				isSpaceAdmin &&
				!checkSpaceAdminError &&
				!hasUserSeenAutomationDiscoveryMessage,
		);
		// Similar to above, this is used to control the automation discovery message in the modal
		if (
			shouldUseAutomationDiscovery &&
			isSpaceAdmin &&
			!checkSpaceAdminError &&
			!hasUserSeenAutomationDiscoveryMessage
		) {
			setIsAutomationDiscoveryMessageVisible(true);
		}
	}, [
		contentId,
		analyticsSource,
		fireButtonClickEvent,
		getAdditionalMutationVariables,
		getAdditionalAnalyticsAttributes,
		includeNestedPages,
		isArchive,
		startMutationCallback,
		shouldUseAutomationDiscovery,
		hasUserSeenAutomationDiscoveryMessage,
		isSpaceAdmin,
		checkSpaceAdminError,
	]);

	const handleClose = useCallback(() => {
		fireButtonClickEvent('Cancel', isArchive, analyticsSource);
		setIsOpen(false);
		experienceTracker.abort({
			name: getExperienceName(isArchive),
			reason: 'dialog closed',
		});
	}, [analyticsSource, fireButtonClickEvent, isArchive, setIsOpen, experienceTracker]);

	const primaryButtonText = useMemo(() => {
		if (isSelectionLimitReached) {
			return isArchive
				? i18n.ARCHIVE_archiveParentButtonSelectionLimitReachedText
				: i18n.UNARCHIVE_restoreParentButtonSelectionLimitReachedText;
		}

		if (isArchive) {
			return isSingleArchive
				? i18n.ARCHIVE_singleArchiveButtonText
				: i18n.ARCHIVE_nestedPagesButtonText;
		} /* !isArchive */ else {
			return i18n.UNARCHIVE_buttonText;
		}
	}, [isArchive, isSingleArchive, isSelectionLimitReached]);

	const showNextButton = !isNestedActionsDialogNotePageEnabled && !isSingleArchive;

	const Footer = useCallback(
		() => (
			<DialogActionsContainer>
				<ButtonGroup>
					<Button
						appearance="subtle"
						onClick={handleClose}
						testId="button-cancel"
						isDisabled={isPageMutationStarted}
					>
						<FormattedMessage {...i18n.cancelButtonText} />
					</Button>
					{showNextButton && setIsNestedActionsDialogNotePageEnabled ? (
						<Button
							appearance="primary"
							onClick={() => setIsNestedActionsDialogNotePageEnabled(true)}
							testId="button-archive-primary"
						>
							<FormattedMessage {...i18n.ARCHIVE_NextButtonText} />
						</Button>
					) : (
						<LoadingButton
							appearance={isSelectionLimitReached ? 'warning' : 'primary'}
							onClick={handleStartArchive}
							testId="button-archive-primary"
							isDisabled={isPageMutationStarted || isSubmitDisabled}
							isLoading={isPageMutationStarted}
						>
							<FormattedMessage {...primaryButtonText} />
						</LoadingButton>
					)}
				</ButtonGroup>
			</DialogActionsContainer>
		),
		[
			handleClose,
			isPageMutationStarted,
			showNextButton,
			isSelectionLimitReached,
			handleStartArchive,
			isSubmitDisabled,
			primaryButtonText,
			setIsNestedActionsDialogNotePageEnabled,
		],
	);

	const headingText = useMemo(() => {
		const ARCHIVE_nestedPagesTitle = i18n.ARCHIVE_nestedPagesTitleGeneric;
		if (!isArchive) {
			// Reminder that for unarchiving (restoring) this component is the 2nd dialog page where the
			// user selects the destination.  We create a similar looking 1st dialog page in
			// UnarchivePageLoader to show the same nested pages info/selection.
			return i18n.UNARCHIVE_pageDialogTitle;
		} else {
			if (error) {
				return ARCHIVE_nestedPagesTitle;
			}
			if (isSingleArchive) {
				return i18n.ARCHIVE_singlePageWithNoteV2;
			} else {
				// archiving with nested pages
				const archivableDescendantsCount = data?.singleContent?.archivableDescendantsCount;
				if (isSelectionLimitReached) {
					return i18n.ARCHIVE_nestedPagesTitleGeneric;
				} else if (archivableDescendantsCount === 0 || !allowNestedPages) {
					// No archive perms for children or standard version of archive
					return i18n.ARCHIVE_withoutNestedPagesTitle;
				} else {
					return ARCHIVE_nestedPagesTitle;
				}
			}
		}
	}, [data, isArchive, isSingleArchive, error, allowNestedPages, isSelectionLimitReached]);

	if (loading && needQueryData) {
		return null;
	}

	const titleAppearance = appearance !== 'primary' ? appearance : undefined;
	const title = loading && isArchive ? '' : <FormattedMessage {...headingText} />;
	const body = descriptionText ? <FormattedMessage {...descriptionText} /> : null;

	const showAutomationDiscoveryMessage =
		isAutomationDiscoveryMessageVisible &&
		shouldUseAutomationDiscovery &&
		!checkSpaceAdminError &&
		isSpaceAdmin &&
		!archivingError &&
		!isPageMutationStarted;

	return isOpen ? (
		<>
			<Modal
				autoFocus={false}
				onClose={handleClose}
				shouldCloseOnEscapePress
				width={`${width}px`}
				shouldScrollInViewport
				messageType="transactional"
				messageId="archive-pages-archive-dialog-base-component"
			>
				{showAutomationDiscoveryMessage ? (
					<LazyArchivePageAutomationDiscovery
						spaceKey={spaceKey}
						closeModal={() => setIsOpen(false)}
					/>
				) : (
					<>
						<ModalHeader
							hasCloseButton={expValEquals('cc_admin_axp_refactor_modals', 'isEnabled', true)}
						>
							<ModalTitle appearance={titleAppearance}>
								{title}
								<ReverseTrialLozengeExperiment
									touchpointId="confluence_archive_dialog_header"
									edition={ConfluenceEdition.STANDARD}
								/>
							</ModalTitle>
						</ModalHeader>
						<ModalBody>
							{body}
							{children}
						</ModalBody>
						<ModalFooter>{Footer()}</ModalFooter>
					</>
				)}
			</Modal>
			{/* TypeScript cannot see the types correctly if these errors are combined
       but in any case, these would both return null for any errors */}
			{error ? <ErrorDisplay error={error} /> : null}
			{checkSpaceAdminError ? <ErrorDisplay error={checkSpaceAdminError} /> : null}
		</>
	) : null;
};
